import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Calendar, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Riders = () => {
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    vehicle_number: "",
    telegram_chat_id: "",
  });

  useEffect(() => {
    fetchRiders();
  }, []);

  const fetchRiders = async () => {
    try {
      const response = await axios.get(`${API}/riders`);
      setRiders(response.data);
    } catch (error) {
      console.error("Error fetching riders:", error);
      toast.error("Failed to fetch riders");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRider = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/riders`, formData);
      toast.success("Rider added successfully");
      setDialogOpen(false);
      setFormData({
        name: "",
        phone: "",
        vehicle_number: "",
        telegram_chat_id: "",
      });
      fetchRiders();
    } catch (error) {
      console.error("Error creating rider:", error);
      toast.error("Failed to add rider");
    }
  };

  const handleDeleteRider = async (riderId, riderName) => {
    if (!window.confirm(`Are you sure you want to delete ${riderName}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/riders/${riderId}`);
      toast.success("Rider deleted successfully");
      fetchRiders();
    } catch (error) {
      console.error("Error deleting rider:", error);
      toast.error("Failed to delete rider");
    }
  };

  const getNoServiceDay = (vehicleNumber) => {
    if (!vehicleNumber) return null;
    const lastDigit = vehicleNumber.slice(-1);
    const dayMap = {
      1: "Monday",
      2: "Monday",
      3: "Tuesday",
      4: "Tuesday",
      5: "Wednesday",
      6: "Wednesday",
      7: "Thursday",
      8: "Thursday",
      9: "Friday",
      0: "Friday",
    };
    return dayMap[lastDigit] || "Saturday (All riders can service)";
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="riders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="riders-title">
            Riders
          </h1>
          <p className="text-slate-500 mt-2">Manage delivery riders</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-orange-500 text-white hover:bg-orange-600 shadow-sm" data-testid="add-rider-btn">
              <Plus size={20} className="mr-2" />
              Add Rider
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-rider-dialog">
            <DialogHeader>
              <DialogTitle>Add New Rider</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateRider} className="space-y-4">
              <div>
                <Label htmlFor="name">Rider Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="input-rider-name"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  data-testid="input-rider-phone"
                />
              </div>
              <div>
                <Label htmlFor="vehicle_number">Vehicle Number</Label>
                <Input
                  id="vehicle_number"
                  value={formData.vehicle_number}
                  onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })}
                  required
                  placeholder="e.g., ABC123"
                  data-testid="input-vehicle-number"
                />
              </div>
              <div>
                <Label htmlFor="telegram_chat_id">Telegram Chat ID (Optional)</Label>
                <Input
                  id="telegram_chat_id"
                  value={formData.telegram_chat_id}
                  onChange={(e) => setFormData({ ...formData, telegram_chat_id: e.target.value })}
                  placeholder="e.g., 123456789"
                  data-testid="input-telegram-id"
                />
              </div>
              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="submit-rider-btn">
                Add Rider
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Service Day Legend */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4" data-testid="service-day-legend">
        <h3 className="text-sm font-bold text-blue-900 mb-2 flex items-center gap-2">
          <Calendar size={16} />
          Vehicle Number Based Service Days
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-blue-800">
          <div>Numbers ending 1, 2: <strong>Monday OFF</strong></div>
          <div>Numbers ending 3, 4: <strong>Tuesday OFF</strong></div>
          <div>Numbers ending 5, 6: <strong>Wednesday OFF</strong></div>
          <div>Numbers ending 7, 8: <strong>Thursday OFF</strong></div>
          <div>Numbers ending 9, 0: <strong>Friday OFF</strong></div>
          <div>Saturday: <strong>ALL RIDERS CAN SERVICE</strong></div>
        </div>
      </div>

      {/* Riders Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="riders-grid">
        {riders.length === 0 ? (
          <div className="col-span-full text-center py-12 text-slate-500">
            No riders found. Add your first rider to get started.
          </div>
        ) : (
          riders.map((rider) => (
            <div
              key={rider.id}
              className="bg-white border border-slate-200 shadow-sm rounded-md p-6 hover:border-slate-300 transition-all"
              data-testid="rider-card"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {rider.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">{rider.name}</h3>
                    <p className="text-sm text-slate-500">{rider.phone}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteRider(rider.id, rider.name)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                  data-testid="delete-rider-btn"
                >
                  <Trash2 size={18} />
                </button>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Vehicle Number:</span>
                  <span className="font-medium text-slate-900">{rider.vehicle_number}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">No Service Day:</span>
                  <span className="font-medium text-amber-600">
                    {getNoServiceDay(rider.vehicle_number)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Status:</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      rider.active
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {rider.active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              <Button
                onClick={() => navigate(`/riders/${rider.id}`)}
                className="w-full bg-slate-900 text-white hover:bg-slate-800"
                data-testid="view-dashboard-btn"
              >
                <ExternalLink size={16} className="mr-2" />
                View Dashboard
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Riders;
